// Phase 197 — CashFlowForecast.jsx
//
// "未來 30 日現金流預測" — Dashboard widget that projects the user's
// balance over the next 30 days based on their active recurring rules.
// Read-only in v1: no what-if controls, no scenario editor. Just the
// pure-function forecast, visualized.
//
// Contract:
//   - rules: array of recurring rules (already fetched by the caller —
//     this component does not fetch anything).
//   - currentBalance: the user's total balance as of `today` (caller
//     computes this from transactions, mirrors the existing
//     computeNetWorth pattern).
//   - today: Date (defaults to `new Date()`)
//   - currency: display symbol passed to formatMoney
//
// If there are no ACTIVE rules (filter r.active !== false), the widget
// shows an empty-state CTA pointing to the recurring settings page.

import React, { useMemo } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { formatMoney } from '../lib/format.js';
import { useI18n } from '../i18n/index.jsx';
import { computeCashFlowForecast } from '../lib/forecast.js';

const COLOR_LINE = '#818cf8';      // indigo-400
const COLOR_INCOME = '#10b981';    // emerald-500
const COLOR_EXPENSE = '#f87171';   // red-400
const COLOR_MUTED = '#94a3b8';     // slate-400
const COLOR_TODAY = '#64748b';     // slate-500

// Reusable card surface — matches the GoalsSummary / credit-card widget
// pattern in Dashboard.jsx (Phase 50+). Tailwind-only.
function Card({ children, testId }) {
  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export default function CashFlowForecast({
  rules = [],
  currentBalance = 0,
  today = new Date(),
  currency = 'HKD',
  days = 30,
}) {
  const { t } = useI18n();

  // Filter to ACTIVE rules only. Inactive rules are excluded from the
  // forecast (matches recurring engine's "active flag respected" rule).
  const activeRules = useMemo(
    () => rules.filter(r => r.active !== false),
    [rules]
  );

  const points = useMemo(
    () => computeCashFlowForecast({
      rules: activeRules,
      today,
      currentBalance,
      days,
    }),
    [activeRules, today, currentBalance, days]
  );

  // Empty state: 0 active rules → CTA, no chart.
  if (activeRules.length === 0) {
    return (
      <Card testId="cashflow-forecast-empty">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          {t('dashboard.forecast.empty.title')}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-3">
          {t('dashboard.forecast.empty.body')}
        </p>
        <a
          href="#/recurring"
          className="inline-flex items-center text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline min-h-[44px] px-2"
        >
          {t('dashboard.forecast.empty.cta')}
        </a>
      </Card>
    );
  }

  const todayPoint = points[0];
  const endPoint = points[points.length - 1];
  const delta30 = endPoint.balance - todayPoint.balance;
  const incomeCount = activeRules.filter(r => r.type === 'income').length;
  const expenseCount = activeRules.filter(r => r.type === 'expense').length;

  // recharts expects { x, balance } shape
  const chartData = points.map((p, i) => ({ x: i, iso: p.iso, label: p.label, balance: p.balance, events: p.events }));

  return (
    <Card testId="cashflow-forecast">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
        {t('dashboard.forecast.title')}
      </h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3">
        {t('dashboard.forecast.subtitle')}
      </p>

      {/* Stat row: today, +30, active count */}
      <div className="flex gap-6 mb-3 flex-wrap">
        <Stat label={t('dashboard.forecast.todayBalance')} value={formatMoney(todayPoint.balance, currency)} />
        <Stat
          label={t('dashboard.forecast.projection30d')}
          value={formatMoney(endPoint.balance, currency)}
          sub={
            <span className={delta30 >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
              {(delta30 >= 0 ? '+' : '') + formatMoney(delta30, currency)}{' '}
              {t('dashboard.forecast.delta')}
            </span>
          }
        />
        <Stat
          label={t('dashboard.forecast.activeCount', { n: activeRules.length })}
          sub={
            <span className="text-slate-500 dark:text-slate-400">
              {incomeCount} {t('dashboard.forecast.income')} · {expenseCount} {t('dashboard.forecast.expense')}
            </span>
          }
        />
      </div>

        {/* Chart */}
        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cfArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#818cf8" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#818cf8" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: COLOR_MUTED }}
                stroke={COLOR_MUTED}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: COLOR_MUTED }}
                stroke={COLOR_MUTED}
                width={50}
                tickFormatter={(v) => formatMoney(v, currency)}
              />
              <Tooltip content={<ForecastTooltip currency={currency} t={t} />} />
              <ReferenceLine x={0} stroke={COLOR_TODAY} strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#818cf8"
                strokeWidth={2}
                fill="url(#cfArea)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-2 text-xs text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_LINE }} />
            {t('dashboard.forecast.legend.balance')}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_INCOME }} />
            <span className="text-emerald-600 dark:text-emerald-400">{t('dashboard.forecast.income')}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_EXPENSE }} />
            <span className="text-red-600 dark:text-red-400">{t('dashboard.forecast.expense')}</span>
          </span>
      </div>
    </Card>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      <div className="text-lg font-semibold mt-0.5 tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
      {sub && <div className="text-[11px] mt-0.5">{sub}</div>}
    </div>
  );
}

function ForecastTooltip({ active, payload, currency, t }) {
  if (!active || !payload || !payload[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2.5 py-2 text-xs shadow-md">
      <div className="text-slate-500 dark:text-slate-400 mb-1">{p.iso}</div>
      <div className="flex justify-between gap-3">
        <span>{t('dashboard.forecast.tooltip.balance')}</span>
        <span className="font-semibold tabular-nums">{formatMoney(p.balance, currency)}</span>
      </div>
      {p.events && p.events.length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-700 mt-1.5 pt-1.5 space-y-0.5">
          {p.events.map((ev, i) => (
            <div key={i} className="flex justify-between gap-3">
              <span className={ev.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                {(ev.type === 'income' ? '+' : '−') + formatMoney(ev.amount, currency)}
              </span>
              <span className="text-slate-500 dark:text-slate-400">{ev.note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}